import React, { useEffect, useRef, useState } from 'react';
import { Box, HStack, VStack } from 'native-base';
import { Caption } from '~/shared/Typography';
import SegmentedInput from '~/shared/SegmentedInput';
import FooterTemplate from './FooterTemplate';
import { useI18n, useTheme } from '@springbig/shared';
import ResendCodeButton from './ResendCodeButton';

const LoginTemplate = React.memo( ({
    codeLength,
    handleSubmit,
    pinError,
    disabled,
    phoneNumber,
    handleResendCode,
    isResendCode,
}: {
    codeLength: number;
    handleSubmit: (value: any) => void;
    pinError: string;
    disabled: boolean;
    phoneNumber: string;
    handleResendCode: () => void;
    isResendCode: boolean;
}) => {

    const {
        text,
        button_text,
    } = useTheme();

    const { t } = useI18n();

    const formatPhoneNumber = (phone: string): string => {

        if (phone) {
            const areaCode = phone?.slice(0, 3);
            const centralOfficeCode = phone?.slice(3, 6);
            const lineNumber = phone?.slice(6);
            return `(${areaCode}) ${centralOfficeCode}-${lineNumber}`;
        }

        return '';
    };

    const [countdown, setCountdown] = useState<number>(0);
    const countdownRef = useRef<NodeJS.Timer | null>(null);

    useEffect(() => {
        if (countdown > 0) {
            countdownRef.current = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownRef.current!);
                        return 0;
                    }

                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
            }
        };
    }, [countdown]);

    useEffect(() => {

        let timer;

        if (disabled) {
            setCountdown(30);
            timer = setTimeout(() => {

            }, 60 * 1000);

            return () => clearTimeout(timer);
        }
    }, [disabled]);

    return (
        <VStack justifyContent="flex-start" flex={1} space={4}>
            <VStack space={4}>
                <VStack>
                    <Caption
                        text={'loginTemplate.enter_code_title'}
                        fontSize={'24'}
                        fontWeight={'bold'}
                        fontFamily={'Helvetica'}
                    />
                    <Caption
                        text={'loginTemplate.enter_code_subtitle'}
                        textValues={[codeLength]}
                        fontFamily={'Helvetica'}
                    />
                </VStack>
                <SegmentedInput
                    codeLength={codeLength}
                    onSubmit={handleSubmit}
                    error={pinError}
                />
                <HStack>
                    <Box
                        alignSelf={'center'}
                        h={'1px'}
                        backgroundColor={text}
                        flex={1}
                        flexDir={'row'}
                    />
                    <Caption
                        text={'loginTemplate.or_separator'}
                        px={4}
                        textAlign={'center'}
                    />
                    <Box
                        alignSelf={'center'}
                        h={'1px'}
                        backgroundColor={text}
                        flex={1}
                        flexDir={'row'}
                    />
                </HStack>
                <Caption
                    fontFamily="Helvetica"
                    textAlign="center"
                >
                    { !disabled && phoneNumber === ''
                        ? t('loginTemplate.no_phone_number_message')
                        : t('loginTemplate.phone_number_message', [
                            formatPhoneNumber(phoneNumber),
                        ])
                    }
                </Caption>
                <ResendCodeButton
                    countdown={countdown}
                    disabled={disabled}
                    handleResendCode={handleResendCode}
                    isResendCode={isResendCode}
                    buttonTextColor={button_text}
                    t={t}
                />
            </VStack>
            <FooterTemplate />
        </VStack>
    );
});

LoginTemplate.displayName = 'LoginTemplate';


export default LoginTemplate;
